/**
 * Pages d'authentification du compte client boutique B2C.
 * - /boutique/inscription
 * - /boutique/login
 * - /boutique/mot-de-passe-oublie
 * - /boutique/reinitialiser/:token
 * - /boutique/verifier-email/:token
 * - /boutique/compte
 *
 * Charte visuelle = même que Shop.jsx (fond #0A0A0A, doré #C1A063, basilic #88A066).
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Mail, Lock, User, Phone, MapPin, AlertCircle, CheckCircle2, Loader2, LogOut, KeyRound, ChevronRight, ChevronLeft, Package, Receipt, FileText, Shield, Briefcase, Building2, Store, Tag, Search, Flame } from "lucide-react";
import { useShopAuth, shopApi, formatShopAuthError } from "../lib/shopAuth";
import { ResellerSpace } from "../components/shop-account/ResellerSpace";
import { AddressBook } from "../components/shop-account/AddressBook";
import { ReorderButton } from "../components/shop-account/ReorderButton";

// Couleurs (sync avec Shop.jsx)
const COLORS = {
  bg: "#0A0A0A",
  surface: "#141414",
  surfaceHover: "#1F1F1F",
  primary: "#F2ECE4",
  secondary: "#A6A29C",
  muted: "#737373",
  chili: "#E54D2E",
  gold: "#C1A063",
  basil: "#88A066",
};

// ============== Composants atomiques partagés ==============
function Field({ icon: Icon, label, children, testid }) {
  return (
    <label className="block mb-4" data-testid={testid}>
      <span
        className="block text-[10px] uppercase tracking-[0.2em] mb-2"
        style={{ color: COLORS.secondary }}
      >
        {label}
      </span>
      <div className="relative">
        {Icon && (
          <Icon
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
            style={{ color: COLORS.muted }}
          />
        )}
        {children}
      </div>
    </label>
  );
}

function Input({ icon, ...props }) {
  return (
    <input
      {...props}
      className={`w-full bg-transparent border px-${icon ? "10" : "3"} py-3 font-body text-sm placeholder:text-[#5a5a5a] focus:outline-none focus:border-[#C1A063] transition-colors`}
      style={{
        borderColor: "rgba(242,236,228,0.18)",
        color: COLORS.primary,
        paddingLeft: icon ? "2.5rem" : "0.75rem",
      }}
    />
  );
}

function PrimaryButton({ children, loading, disabled, ...props }) {
  return (
    <button
      {...props}
      disabled={loading || disabled}
      className="w-full px-6 py-3 font-body uppercase tracking-[0.2em] text-xs transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      style={{
        background: COLORS.gold,
        color: COLORS.bg,
        border: `1px solid ${COLORS.gold}`,
        fontWeight: 600,
      }}
    >
      {loading ? (
        <span className="inline-flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          En cours…
        </span>
      ) : children}
    </button>
  );
}

function Alert({ kind = "error", children, testid }) {
  const isErr = kind === "error";
  const Icon = isErr ? AlertCircle : CheckCircle2;
  return (
    <div
      data-testid={testid}
      className="flex items-start gap-2 p-3 mb-4 text-sm"
      style={{
        background: isErr ? "rgba(229,77,46,0.08)" : "rgba(136,160,102,0.10)",
        border: `1px solid ${isErr ? "rgba(229,77,46,0.35)" : "rgba(136,160,102,0.4)"}`,
        color: isErr ? COLORS.chili : COLORS.basil,
      }}
    >
      <Icon className="w-4 h-4 mt-0.5 shrink-0" />
      <span className="leading-relaxed">{children}</span>
    </div>
  );
}

function AuthShell({ title, subtitle, children }) {
  return (
    <section className="min-h-[70vh] flex items-center justify-center px-4 sm:px-6 py-12 md:py-20">
      <div className="w-full max-w-md">
        {/* Phase 98i — Lien retour boutique en haut de toute page shop-auth */}
        <Link
          to="/boutique"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] mb-4 transition-colors hover:opacity-80"
          style={{ color: COLORS.gold }}
          data-testid="auth-back-to-shop"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          Retour boutique
        </Link>
        <div
          style={{ background: COLORS.surface, border: "1px solid rgba(242,236,228,0.06)" }}
        >
          <div className="px-6 sm:px-8 py-8">
            <p
              className="text-[10px] tracking-[0.3em] uppercase mb-2"
              style={{ color: COLORS.gold }}
            >
              Boutique Maison Thaï
            </p>
            <h1
              className="text-2xl sm:text-3xl mb-2"
              style={{ color: COLORS.primary, fontFamily: "Georgia, serif", fontWeight: 600 }}
            >
              {title}
            </h1>
            {subtitle && (
              <p className="text-sm mb-6" style={{ color: COLORS.secondary }}>{subtitle}</p>
            )}
            {children}
          </div>
        </div>
      </div>
    </section>
  );
}

// ============== Page : Inscription ==============
export function ShopRegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, customer } = useShopAuth();
  // Phase 98h — Type de compte : lu depuis query param ?type=pro (venant du bouton
  // "Devenir revendeur"), sinon par défaut "particulier".
  const initialType = searchParams.get("type") === "pro" ? "pro" : "particulier";
  const [form, setForm] = useState({
    email: "", password: "", name: "", phone: "",
    address: "", postal_code: "", city: "",
    account_type: initialType,
    business_name: "",
    business_type: "",
    expected_volume: "",
    business_message: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successMode, setSuccessMode] = useState("email");  // "email" | "auto_verified" | "pro_pending"

  useEffect(() => {
    // Si déjà connecté, on redirige vers le compte
    if (customer && customer !== false) navigate("/boutique/compte", { replace: true });
  }, [customer, navigate]);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const setAccountType = (t) => setForm({ ...form, account_type: t });
  const isPro = form.account_type === "pro";

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await register(form);
      setSuccess(true);
      if (result?.customer?.pro_status === "pending") {
        setSuccessMode("pro_pending");
      } else if (result?.auto_verified) {
        setSuccessMode("auto_verified");
      } else {
        setSuccessMode("email");
      }
    } catch (err) {
      setError(formatShopAuthError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    const titles = {
      pro_pending: "Compte pro en attente",
      auto_verified: "Compte activé",
      email: "Vérifiez votre email",
    };
    const subtitles = {
      pro_pending: "Votre candidature a bien été enregistrée.",
      auto_verified: "Votre compte est prêt à l'emploi.",
      email: "Une dernière étape avant de commander.",
    };
    return (
      <AuthShell title={titles[successMode]} subtitle={subtitles[successMode]}>
        {successMode === "pro_pending" && (
          <>
            <Alert kind="success" testid="register-pro-pending">
              <span>
                Bienvenue ! Nous avons bien reçu votre demande de compte <strong>professionnel</strong>{" "}
                à <strong>{form.email}</strong>.
                <br /><br />
                Un email de vérification vous a été envoyé. Notre équipe examinera votre candidature dans les 24-48h et vous notifiera par email.
                <br /><br />
                <strong>En attendant :</strong> vous pouvez vous connecter et parcourir la boutique, mais le passage de commande sera possible <strong>uniquement après validation</strong> de votre compte pro.
              </span>
            </Alert>
            <p className="text-xs mt-4" style={{ color: COLORS.muted }}>
              Une question ? Écrivez-nous à{" "}
              <a href="mailto:info@maison-thai.ch" className="underline" style={{ color: COLORS.gold }}>info@maison-thai.ch</a>
            </p>
          </>
        )}
        {successMode === "auto_verified" && (
          <>
            <Alert kind="success" testid="register-auto-verified">
              <span>Votre compte est activé. Vous pouvez vous connecter.</span>
            </Alert>
            <p className="text-xs mt-4" style={{ color: COLORS.muted }}>
              <Link to="/boutique/login" className="underline" style={{ color: COLORS.gold }}>Aller à la connexion</Link>
            </p>
          </>
        )}
        {successMode === "email" && (
          <>
            <Alert kind="success" testid="register-success">
              <span>
                Nous venons de vous envoyer un email à <strong>{form.email}</strong> pour
                confirmer votre adresse. Cliquez sur le lien (valable 48h) pour activer votre
                compte et accéder à votre première commande.
              </span>
            </Alert>
            <p className="text-xs mt-4" style={{ color: COLORS.muted }}>
              Vous ne voyez pas l&apos;email ? Vérifiez vos spams ou{" "}
              <Link to="/boutique/login" className="underline" style={{ color: COLORS.gold }}>
                connectez-vous pour demander un nouveau lien
              </Link>.
            </p>
          </>
        )}
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={isPro ? "Créer un compte pro" : "Créer un compte"}
      subtitle={
        isPro
          ? "Réservé aux revendeurs, magasins, kiosques et restaurateurs — soumis à validation."
          : "Pour suivre vos commandes et payer plus vite la prochaine fois."
      }
    >
      <form onSubmit={onSubmit} data-testid="register-form">
        {/* Phase 98h — Toggle Particulier / Pro en haut du formulaire */}
        <div className="grid grid-cols-2 gap-2 mb-6" data-testid="register-account-type-toggle">
          <button
            type="button"
            onClick={() => setAccountType("particulier")}
            className="px-4 py-3 text-xs uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-2"
            style={{
              background: form.account_type === "particulier" ? COLORS.gold : "transparent",
              color: form.account_type === "particulier" ? COLORS.bg : COLORS.primary,
              border: `1px solid ${form.account_type === "particulier" ? COLORS.gold : "rgba(242,236,228,0.18)"}`,
              fontWeight: 600,
            }}
            data-testid="register-type-particulier"
          >
            <User className="w-4 h-4" />
            Particulier
          </button>
          <button
            type="button"
            onClick={() => setAccountType("pro")}
            className="px-4 py-3 text-xs uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-2"
            style={{
              background: form.account_type === "pro" ? COLORS.gold : "transparent",
              color: form.account_type === "pro" ? COLORS.bg : COLORS.primary,
              border: `1px solid ${form.account_type === "pro" ? COLORS.gold : "rgba(242,236,228,0.18)"}`,
              fontWeight: 600,
            }}
            data-testid="register-type-pro"
          >
            <Briefcase className="w-4 h-4" />
            Professionnel
          </button>
        </div>
        {isPro && (
          <p className="text-xs mb-4 leading-relaxed" style={{ color: COLORS.secondary }}>
            Un compte pro donne accès à des tarifs négociés après validation par notre équipe (24-48h). L&apos;achat en attendant reste disponible uniquement pour les particuliers.
          </p>
        )}

        {error && <Alert testid="register-error">{error}</Alert>}

        <Field icon={User} label={isPro ? "Nom du contact" : "Nom complet"} testid="register-name-field">
          <Input icon name="name" value={form.name} onChange={onChange} required placeholder="Jean Dupont" data-testid="register-name-input" />
        </Field>

        {isPro && (
          <>
            <Field icon={Building2} label="Nom de l'entreprise / boutique" testid="register-business-name-field">
              <Input icon name="business_name" value={form.business_name} onChange={onChange} required placeholder="Kiosque des Alpes SA" data-testid="register-business-name-input" />
            </Field>
            <Field icon={Briefcase} label="Type d'activité" testid="register-business-type-field">
              <select
                name="business_type"
                value={form.business_type}
                onChange={onChange}
                data-testid="register-business-type-input"
                className="w-full bg-transparent border px-3 py-3 font-body text-sm focus:outline-none focus:border-[#C1A063] transition-colors"
                style={{
                  borderColor: "rgba(242,236,228,0.18)",
                  color: COLORS.primary,
                }}
              >
                <option value="" style={{ background: COLORS.bg }}>— Sélectionnez —</option>
                <option value="magasin_alimentaire" style={{ background: COLORS.bg }}>Magasin alimentaire</option>
                <option value="kiosque" style={{ background: COLORS.bg }}>Kiosque</option>
                <option value="restaurant" style={{ background: COLORS.bg }}>Restaurant / traiteur</option>
                <option value="shop" style={{ background: COLORS.bg }}>Shop / boutique</option>
                <option value="autre" style={{ background: COLORS.bg }}>Autre</option>
              </select>
            </Field>
            <Field icon={Package} label="Volume estimé (barquettes/mois)" testid="register-volume-field">
              <select
                name="expected_volume"
                value={form.expected_volume}
                onChange={onChange}
                data-testid="register-volume-input"
                className="w-full bg-transparent border px-3 py-3 font-body text-sm focus:outline-none focus:border-[#C1A063] transition-colors"
                style={{
                  borderColor: "rgba(242,236,228,0.18)",
                  color: COLORS.primary,
                }}
              >
                <option value="" style={{ background: COLORS.bg }}>— Sélectionnez —</option>
                <option value="10-50" style={{ background: COLORS.bg }}>10 à 50 barquettes / mois</option>
                <option value="50-200" style={{ background: COLORS.bg }}>50 à 200 barquettes / mois</option>
                <option value="200+" style={{ background: COLORS.bg }}>Plus de 200 barquettes / mois</option>
              </select>
            </Field>
          </>
        )}

        <Field icon={Mail} label="Email" testid="register-email-field">
          <Input icon name="email" type="email" autoComplete="email" value={form.email} onChange={onChange} required placeholder={isPro ? "contact@monentreprise.ch" : "vous@exemple.ch"} data-testid="register-email-input" />
        </Field>
        <Field icon={Lock} label="Mot de passe (min. 8 caractères)" testid="register-password-field">
          <Input icon name="password" type="password" autoComplete="new-password" minLength={8} value={form.password} onChange={onChange} required placeholder="••••••••" data-testid="register-password-input" />
        </Field>
        <Field icon={Phone} label="Téléphone" testid="register-phone-field">
          <Input icon name="phone" type="tel" autoComplete="tel" value={form.phone} onChange={onChange} placeholder="+41 79 123 45 67" data-testid="register-phone-input" />
        </Field>

        <p className="text-[10px] tracking-[0.2em] uppercase mt-6 mb-3" style={{ color: COLORS.secondary }}>
          {isPro ? "Adresse professionnelle" : "Adresse (facultatif — vous pourrez la compléter au checkout)"}
        </p>
        <Field icon={MapPin} label="Adresse" testid="register-address-field">
          <Input icon name="address" autoComplete="street-address" value={form.address} onChange={onChange} placeholder="Rue, numéro" required={isPro} data-testid="register-address-input" />
        </Field>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <label className="block col-span-1">
            <span className="block text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: COLORS.secondary }}>NPA</span>
            <Input name="postal_code" autoComplete="postal-code" value={form.postal_code} onChange={onChange} placeholder="1964" required={isPro} data-testid="register-postal-input" />
          </label>
          <label className="block col-span-2">
            <span className="block text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: COLORS.secondary }}>Ville</span>
            <Input name="city" autoComplete="address-level2" value={form.city} onChange={onChange} placeholder="Conthey" required={isPro} data-testid="register-city-input" />
          </label>
        </div>

        {isPro && (
          <label className="block mb-4" data-testid="register-business-message-field">
            <span className="block text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: COLORS.secondary }}>
              Message (optionnel — présentation, projet, questions…)
            </span>
            <textarea
              name="business_message"
              value={form.business_message}
              onChange={onChange}
              rows={3}
              className="w-full bg-transparent border px-3 py-3 font-body text-sm focus:outline-none focus:border-[#C1A063] transition-colors placeholder:text-[#5a5a5a]"
              style={{
                borderColor: "rgba(242,236,228,0.18)",
                color: COLORS.primary,
              }}
              placeholder="Parlez-nous de votre projet, votre positionnement, la zone géographique…"
              data-testid="register-business-message-input"
            />
          </label>
        )}

        <div className="mt-6">
          <PrimaryButton type="submit" loading={loading} data-testid="register-submit">
            {isPro ? "Envoyer ma demande pro" : "Créer mon compte"}
          </PrimaryButton>
        </div>
        <p className="text-center text-sm mt-6" style={{ color: COLORS.secondary }}>
          Déjà inscrit ?{" "}
          <Link to="/boutique/login" className="underline" style={{ color: COLORS.gold }} data-testid="register-to-login">
            Connexion
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}

// ============== Page : Login ==============
export function ShopLoginPage() {
  const navigate = useNavigate();
  const { login, customer } = useShopAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (customer && customer !== false) navigate("/boutique/compte", { replace: true });
  }, [customer, navigate]);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(form.email, form.password);
      // Si on vient du checkout, retour au panier ; sinon compte
      const next = new URLSearchParams(window.location.search).get("next");
      navigate(next || "/boutique/compte", { replace: true });
    } catch (err) {
      setError(formatShopAuthError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Connexion" subtitle="Heureux de vous revoir.">
      <form onSubmit={onSubmit} data-testid="login-form">
        {error && <Alert testid="login-error">{error}</Alert>}
        <Field icon={Mail} label="Email" testid="login-email-field">
          <Input icon name="email" type="email" autoComplete="email" value={form.email} onChange={onChange} required placeholder="vous@exemple.ch" data-testid="login-email-input" />
        </Field>
        <Field icon={Lock} label="Mot de passe" testid="login-password-field">
          <Input icon name="password" type="password" autoComplete="current-password" value={form.password} onChange={onChange} required placeholder="••••••••" data-testid="login-password-input" />
        </Field>
        <div className="text-right -mt-2 mb-4">
          <Link to="/boutique/mot-de-passe-oublie" className="text-xs underline" style={{ color: COLORS.gold }} data-testid="login-to-forgot">
            Mot de passe oublié ?
          </Link>
        </div>
        <PrimaryButton type="submit" loading={loading} data-testid="login-submit">
          Se connecter
        </PrimaryButton>
        <p className="text-center text-sm mt-6" style={{ color: COLORS.secondary }}>
          Pas encore de compte ?{" "}
          <Link to="/boutique/inscription" className="underline" style={{ color: COLORS.gold }} data-testid="login-to-register">
            Créer un compte
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}

// ============== Page : Mot de passe oublié ==============
export function ShopForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await shopApi.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatShopAuthError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <AuthShell title="Email envoyé" subtitle="Si un compte existe, le lien arrive.">
        <Alert kind="success" testid="forgot-sent">
          Si un compte existe pour cette adresse, un lien de réinitialisation vient
          d&apos;être envoyé. Vérifiez votre boîte de réception (et vos spams) — le
          lien expire dans 1 heure.
          <div className="mt-3 text-xs opacity-70">
            Vous ne recevez rien après 5 minutes ? Vérifiez l&apos;adresse saisie
            (elle doit correspondre au compte créé sur la boutique).
          </div>
        </Alert>
        <Link to="/boutique/login" className="block text-center text-sm underline mt-6" style={{ color: COLORS.gold }} data-testid="forgot-back-login">
          ← Retour à la connexion
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Mot de passe oublié" subtitle="Entrez votre email — nous vous envoyons un lien.">
      <form onSubmit={onSubmit} data-testid="forgot-form">
        {error && <Alert testid="forgot-error">{error}</Alert>}
        <Field icon={Mail} label="Email" testid="forgot-email-field">
          <Input icon name="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="vous@exemple.ch" data-testid="forgot-email-input" />
        </Field>
        <PrimaryButton type="submit" loading={loading} data-testid="forgot-submit">
          Envoyer le lien
        </PrimaryButton>
        <Link to="/boutique/login" className="block text-center text-sm underline mt-6" style={{ color: COLORS.gold }} data-testid="forgot-back-login">
          ← Retour à la connexion
        </Link>
      </form>
    </AuthShell>
  );
}

// ============== Page : Réinitialiser mot de passe ==============
export function ShopResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }
    setLoading(true);
    try {
      await shopApi.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/boutique/login", { replace: true }), 2500);
    } catch (err) {
      setError(formatShopAuthError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <AuthShell title="Mot de passe modifié" subtitle="Vous allez être redirigé vers la connexion…">
        <Alert kind="success" testid="reset-done">
          Votre mot de passe a été réinitialisé avec succès.
        </Alert>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Nouveau mot de passe" subtitle="Choisissez un mot de passe d'au moins 8 caractères.">
      <form onSubmit={onSubmit} data-testid="reset-form">
        {error && <Alert testid="reset-error">{error}</Alert>}
        <Field icon={Lock} label="Nouveau mot de passe" testid="reset-password-field">
          <Input icon name="password" type="password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="reset-password-input" />
        </Field>
        <Field icon={Lock} label="Confirmer" testid="reset-confirm-field">
          <Input icon name="confirm" type="password" minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="reset-confirm-input" />
        </Field>
        <PrimaryButton type="submit" loading={loading} data-testid="reset-submit">
          Réinitialiser
        </PrimaryButton>
      </form>
    </AuthShell>
  );
}

// ============== Page : Vérification email ==============
export function ShopVerifyEmailPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState("loading"); // loading | success | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await shopApi.post("/auth/verify-email", { token });
        if (!mounted) return;
        setMessage(data.message || "Email vérifié.");
        setState("success");
        setTimeout(() => navigate("/boutique/login", { replace: true }), 2500);
      } catch (err) {
        if (!mounted) return;
        setMessage(formatShopAuthError(err.response?.data?.detail) || err.message);
        setState("error");
      }
    })();
    return () => { mounted = false; };
  }, [token, navigate]);

  return (
    <AuthShell title="Vérification de votre email">
      {state === "loading" && (
        <div className="flex items-center gap-3 text-sm" style={{ color: COLORS.secondary }} data-testid="verify-loading">
          <Loader2 className="w-4 h-4 animate-spin" />
          Vérification en cours…
        </div>
      )}
      {state === "success" && (
        <Alert kind="success" testid="verify-success">
          {message} Vous allez être redirigé vers la page de connexion.
        </Alert>
      )}
      {state === "error" && (
        <>
          <Alert testid="verify-error">{message}</Alert>
          <Link to="/boutique/login" className="block text-center text-sm underline mt-6" style={{ color: COLORS.gold }}>
            ← Retour à la connexion
          </Link>
        </>
      )}
    </AuthShell>
  );
}

// ============== Page : Compte ==============
export function ShopAccountPage() {
  const navigate = useNavigate();
  const { customer, reseller, logout, updateProfile } = useShopAuth();

  const [activeTab, setActiveTab] = useState("profil");

  const [profile, setProfile] = useState({
    name: "", phone: "", address: "", postal_code: "", city: "",
  });
  const [profileMsg, setProfileMsg] = useState({ kind: null, text: "" });
  const [savingProfile, setSavingProfile] = useState(false);

  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwdMsg, setPwdMsg] = useState({ kind: null, text: "" });
  const [savingPwd, setSavingPwd] = useState(false);

  const [orders, setOrders] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [invoiceSummary, setInvoiceSummary] = useState({ total_open: 0, count_open: 0 });
  const [resendVerif, setResendVerif] = useState({ loading: false, msg: null });
  // Phase 101fp — Onglet tarifs B2B (revendeurs uniquement)
  const [b2bCatalog, setB2bCatalog] = useState(null);
  const [b2bSearch, setB2bSearch] = useState("");
  const [b2bCategory, setB2bCategory] = useState("__all__");

  useEffect(() => {
    if (customer === false) navigate("/boutique/login", { replace: true });
  }, [customer, navigate]);

  useEffect(() => {
    if (customer && customer !== false) {
      setProfile({
        name: customer.name || "",
        phone: customer.phone || "",
        address: customer.address || "",
        postal_code: customer.postal_code || "",
        city: customer.city || "",
      });
    }
  }, [customer]);

  useEffect(() => {
    if (!customer || customer === false || !customer.email_verified) return;
    shopApi.get("/my-orders").then(({ data }) => setOrders(data)).catch(() => setOrders([]));
  }, [customer]);

  // Onglet "Mes factures" chargé à la demande (revendeurs B2B principalement)
  useEffect(() => {
    if (!customer || customer === false || !customer.email_verified) return;
    if (activeTab !== "factures" || invoices !== null) return;
    shopApi
      .get("/my-invoices")
      .then(({ data }) => {
        setInvoices(data.invoices || []);
        setInvoiceSummary(data.summary || { total_open: 0, count_open: 0 });
      })
      .catch(() => setInvoices([]));
  }, [activeTab, customer, invoices]);

  // Phase 101fp — Onglet Mes tarifs B2B (revendeurs uniquement)
  useEffect(() => {
    if (!customer || customer === false || !customer.email_verified) return;
    if (activeTab !== "b2b" || b2bCatalog !== null) return;
    shopApi
      .get("/my-b2b-catalog")
      .then(({ data }) => setB2bCatalog(data))
      .catch(() => setB2bCatalog({ items: [], reseller: null, total: 0 }));
  }, [activeTab, customer, b2bCatalog]);

  if (customer === null) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="account-loading">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: COLORS.gold }} />
      </div>
    );
  }
  if (customer === false) return null;

  const onSaveProfile = async (e) => {
    e.preventDefault();
    setProfileMsg({ kind: null, text: "" });
    setSavingProfile(true);
    try {
      await updateProfile(profile);
      setProfileMsg({ kind: "success", text: "Profil mis à jour." });
    } catch (err) {
      setProfileMsg({ kind: "error", text: formatShopAuthError(err.response?.data?.detail) || err.message });
    } finally {
      setSavingProfile(false);
    }
  };

  const onChangePwd = async (e) => {
    e.preventDefault();
    setPwdMsg({ kind: null, text: "" });
    if (pwd.new_password !== pwd.confirm) {
      setPwdMsg({ kind: "error", text: "Les mots de passe ne correspondent pas." });
      return;
    }
    setSavingPwd(true);
    try {
      await shopApi.post("/auth/change-password", {
        current_password: pwd.current_password,
        new_password: pwd.new_password,
      });
      setPwd({ current_password: "", new_password: "", confirm: "" });
      setPwdMsg({ kind: "success", text: "Mot de passe modifié." });
    } catch (err) {
      setPwdMsg({ kind: "error", text: formatShopAuthError(err.response?.data?.detail) || err.message });
    } finally {
      setSavingPwd(false);
    }
  };

  const onResendVerif = async () => {
    setResendVerif({ loading: true, msg: null });
    try {
      const { data } = await shopApi.post("/auth/resend-verification", { email: customer.email });
      setResendVerif({ loading: false, msg: { kind: "success", text: data.message || "Email envoyé." } });
    } catch (err) {
      setResendVerif({
        loading: false,
        msg: { kind: "error", text: formatShopAuthError(err.response?.data?.detail) || err.message },
      });
    }
  };

  const isReseller = !!reseller;

  const TABS = [
    { key: "profil", label: "Profil", icon: User },
    { key: "securite", label: "Sécurité", icon: Shield },
    ...(isReseller ? [{ key: "pro", label: "Espace pro", icon: Building2 }] : []),
    { key: "commandes", label: "Mes commandes", icon: Package },
    ...(isReseller ? [] : [{ key: "factures", label: "Mes factures", icon: Receipt }]),
    // Phase 101fp — Tab tarifs B2B visible uniquement pour les revendeurs
    ...(isReseller ? [{ key: "b2b", label: "Mes tarifs B2B", icon: Tag }] : []),
  ];

  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 md:px-12 py-12 md:py-20" data-testid="account-page">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase mb-2" style={{ color: COLORS.gold }}>
            Mon compte
          </p>
          <h1 className="text-3xl md:text-4xl mb-1" style={{ color: COLORS.primary, fontFamily: "Georgia, serif", fontWeight: 600 }}>
            Bonjour {customer.name?.split(" ")[0] || "client"}
          </h1>
          <div className="flex items-center gap-3 flex-wrap mt-1">
            <p className="text-sm" style={{ color: COLORS.secondary }}>{customer.email}</p>
            {isReseller && (
              <span
                className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5"
                style={{
                  background: "rgba(193,160,99,0.10)",
                  border: `1px solid ${COLORS.gold}`,
                  color: COLORS.gold,
                }}
                data-testid="account-reseller-badge"
              >
                Compte revendeur{reseller.discount_percent ? <> &middot; {reseller.discount_percent}% remise</> : null}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to="/boutique"
            className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] px-4 py-2 transition-colors"
            style={{ background: COLORS.gold, color: COLORS.bg, border: `1px solid ${COLORS.gold}`, fontWeight: 600 }}
            data-testid="account-back-to-shop"
          >
            <Store className="w-3.5 h-3.5" />
            Voir la boutique
          </Link>
          <button
            onClick={async () => { await logout(); navigate("/boutique"); }}
            className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] px-4 py-2 transition-colors"
            style={{ color: COLORS.secondary, border: "1px solid rgba(242,236,228,0.18)" }}
            data-testid="account-logout"
          >
            <LogOut className="w-3.5 h-3.5" />
            Déconnexion
          </button>
        </div>
      </div>

      {/* Phase 98h — Bandeau compte pro en attente de validation */}
      {customer.pro_status === "pending" && (
        <div
          className="p-4 mb-6 flex items-start gap-3 text-sm"
          style={{ background: "rgba(193,160,99,0.10)", border: `1px solid ${COLORS.gold}`, color: COLORS.gold }}
          data-testid="account-pro-pending-banner"
        >
          <Briefcase className="w-5 h-5 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-semibold mb-1">Votre compte pro est en cours de validation</p>
            <p style={{ color: COLORS.secondary }}>
              Nous examinons votre candidature (délai typique : 24-48h). Vous serez notifié par email dès qu&apos;elle sera acceptée et vous verrez alors vos tarifs négociés dans la boutique.
              <br /><br />
              <strong>En attendant, le passage de commande est désactivé.</strong> Merci de votre patience.
            </p>
          </div>
        </div>
      )}
      {customer.pro_status === "rejected" && (
        <div
          className="p-4 mb-6 flex items-start gap-3 text-sm"
          style={{ background: "rgba(229,77,46,0.08)", border: "1px solid rgba(229,77,46,0.35)", color: COLORS.chili }}
          data-testid="account-pro-rejected-banner"
        >
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-semibold mb-1">Candidature pro non retenue</p>
            <p style={{ color: COLORS.secondary }}>
              Votre demande de compte professionnel n&apos;a pas été retenue pour le moment. Vous pouvez toujours acheter en tant que particulier aux tarifs standards.
              <br /><br />
              Pour en discuter, contactez-nous à{" "}
              <a href="mailto:info@maison-thai.ch" className="underline" style={{ color: COLORS.gold }}>info@maison-thai.ch</a>.
            </p>
          </div>
        </div>
      )}

      {!customer.email_verified && (
        <div
          className="p-4 mb-6 flex items-start gap-3 text-sm"
          style={{ background: "rgba(229,77,46,0.08)", border: "1px solid rgba(229,77,46,0.35)", color: COLORS.chili }}
          data-testid="account-verify-warning"
        >
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-semibold mb-1">Email non vérifié</p>
            <p style={{ color: COLORS.secondary }}>
              Vous devez vérifier votre email avant de pouvoir commander. Vérifiez votre boîte
              de réception (et vos spams) pour le lien de confirmation.
            </p>
            {resendVerif.msg && (
              <p className="text-xs mt-2" style={{ color: resendVerif.msg.kind === "success" ? COLORS.basil : COLORS.chili }}>
                {resendVerif.msg.text}
              </p>
            )}
            <button
              onClick={onResendVerif}
              disabled={resendVerif.loading}
              className="mt-3 text-xs uppercase tracking-[0.2em] underline disabled:opacity-50"
              style={{ color: COLORS.gold }}
              data-testid="account-resend-verif"
            >
              {resendVerif.loading ? "Envoi…" : "Renvoyer le lien"}
            </button>
          </div>
        </div>
      )}

      {/* Tabs nav */}
      <div
        className="flex flex-wrap gap-0 mb-6 -mx-1"
        style={{ borderBottom: "1px solid rgba(242,236,228,0.10)" }}
        data-testid="account-tabs"
      >
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = activeTab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className="flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-[0.2em] transition-colors -mb-px"
              style={{
                color: active ? COLORS.gold : COLORS.secondary,
                borderBottom: active ? `2px solid ${COLORS.gold}` : "2px solid transparent",
                fontWeight: active ? 600 : 400,
              }}
              data-testid={`tab-${t.key}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
              {t.key === "factures" && invoiceSummary.count_open > 0 && (
                <span
                  className="ml-1 text-[10px] px-1.5 py-0.5"
                  style={{ background: COLORS.chili, color: COLORS.primary, borderRadius: 2 }}
                  data-testid="tab-factures-badge"
                >
                  {invoiceSummary.count_open}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* TAB PROFIL */}
      {activeTab === "profil" && (
        <div style={{ background: COLORS.surface, border: "1px solid rgba(242,236,228,0.06)" }} className="p-6 md:p-8" data-testid="tab-content-profil">
          <h2 className="text-lg mb-5" style={{ color: COLORS.primary, fontFamily: "Georgia, serif" }}>Mon profil</h2>
          <form onSubmit={onSaveProfile} data-testid="account-profile-form" className="max-w-xl">
            {profileMsg.kind && <Alert kind={profileMsg.kind} testid="account-profile-msg">{profileMsg.text}</Alert>}
            <Field icon={User} label="Nom complet">
              <Input icon value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} required data-testid="account-name-input" />
            </Field>
            <Field icon={Phone} label="Téléphone">
              <Input icon value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} data-testid="account-phone-input" />
            </Field>
            <Field icon={MapPin} label="Adresse">
              <Input icon value={profile.address} onChange={(e) => setProfile({ ...profile, address: e.target.value })} data-testid="account-address-input" />
            </Field>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <label className="block col-span-1">
                <span className="block text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: COLORS.secondary }}>NPA</span>
                <Input value={profile.postal_code} onChange={(e) => setProfile({ ...profile, postal_code: e.target.value })} data-testid="account-postal-input" />
              </label>
              <label className="block col-span-2">
                <span className="block text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: COLORS.secondary }}>Ville</span>
                <Input value={profile.city} onChange={(e) => setProfile({ ...profile, city: e.target.value })} data-testid="account-city-input" />
              </label>
            </div>
            <PrimaryButton type="submit" loading={savingProfile} data-testid="account-profile-submit">
              Enregistrer
            </PrimaryButton>
          </form>
        </div>
      )}
      {activeTab === "profil" && <AddressBook />}

      {activeTab === "pro" && isReseller && customer.email_verified && <ResellerSpace resellerName={reseller.name} />}

      {/* TAB SECURITE */}
      {activeTab === "securite" && (
        <div style={{ background: COLORS.surface, border: "1px solid rgba(242,236,228,0.06)" }} className="p-6 md:p-8" data-testid="tab-content-securite">
          <h2 className="text-lg mb-5" style={{ color: COLORS.primary, fontFamily: "Georgia, serif" }}>Changer mon mot de passe</h2>
          <form onSubmit={onChangePwd} data-testid="account-pwd-form" className="max-w-xl">
            {pwdMsg.kind && <Alert kind={pwdMsg.kind} testid="account-pwd-msg">{pwdMsg.text}</Alert>}
            <Field icon={KeyRound} label="Mot de passe actuel">
              <Input icon type="password" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} required data-testid="account-current-pwd" />
            </Field>
            <Field icon={Lock} label="Nouveau mot de passe (min. 8 car.)">
              <Input icon type="password" minLength={8} value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} required data-testid="account-new-pwd" />
            </Field>
            <Field icon={Lock} label="Confirmer le nouveau mot de passe">
              <Input icon type="password" minLength={8} value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} required data-testid="account-confirm-pwd" />
            </Field>
            <PrimaryButton type="submit" loading={savingPwd} data-testid="account-pwd-submit">
              Modifier le mot de passe
            </PrimaryButton>
          </form>
        </div>
      )}

      {/* TAB COMMANDES */}
      {activeTab === "commandes" && customer.email_verified && (
        <div data-testid="tab-content-commandes">
          {orders === null && (
            <div className="flex items-center gap-2 text-sm py-8" style={{ color: COLORS.secondary }}>
              <Loader2 className="w-4 h-4 animate-spin" /> Chargement de vos commandes…
            </div>
          )}
          {orders && orders.length === 0 && (
            <div
              className="p-8 text-sm text-center"
              style={{ background: COLORS.surface, border: "1px solid rgba(242,236,228,0.06)", color: COLORS.secondary }}
              data-testid="orders-empty"
            >
              Vous n&apos;avez encore passé aucune commande.{" "}
              <Link to="/boutique" className="underline" style={{ color: COLORS.gold }}>Découvrir la boutique →</Link>
            </div>
          )}
          {orders && orders.length > 0 && (
            <div style={{ background: COLORS.surface, border: "1px solid rgba(242,236,228,0.06)" }}>
              <ul className="divide-y" style={{ borderColor: "rgba(242,236,228,0.08)" }}>
                {orders.map((o) => (
                  <li
                    key={o.id}
                    className="p-4 md:p-5 flex items-center justify-between gap-4 flex-wrap"
                    data-testid={`order-row-${o.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-mono" style={{ color: COLORS.primary }}>{o.reference}</p>
                        {o.is_b2b && (
                          <span
                            className="text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5"
                            style={{ background: "rgba(193,160,99,0.10)", color: COLORS.gold, border: `1px solid ${COLORS.gold}` }}
                          >
                            B2B
                          </span>
                        )}
                        {o.bl_id && (
                          <span
                            className="text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5"
                            style={{ background: "rgba(136,160,102,0.10)", color: COLORS.basil, border: `1px solid ${COLORS.basil}` }}
                            data-testid={`order-bl-tag-${o.id}`}
                          >
                            BL dispo
                          </span>
                        )}
                        {o.invoice_id && (
                          <span
                            className="text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5"
                            style={{ background: "rgba(193,160,99,0.10)", color: COLORS.gold, border: `1px solid ${COLORS.gold}` }}
                            data-testid={`order-invoice-tag-${o.id}`}
                          >
                            Facturé
                          </span>
                        )}
                      </div>
                      <p className="text-xs mt-1" style={{ color: COLORS.muted }}>
                        {new Date(o.created_at).toLocaleDateString("fr-CH", { year: "numeric", month: "short", day: "numeric" })}
                        {" · "}{o.lines?.filter((l) => l.product_id !== "__shipping__").length || 0} article(s)
                        {" · "}<StatusBadge status={o.status} />
                      </p>
                    </div>
                    <div className="text-right shrink-0 flex items-center gap-4">
                      <p className="font-mono text-sm" style={{ color: COLORS.gold }}>{o.total_ttc?.toFixed(2)} CHF</p>
                      <ReorderButton lines={o.lines} testid={`order-reorder-${o.id}`} />
                      <Link
                        to={`/boutique/compte/commandes/${o.id}`}
                        className="text-xs uppercase tracking-[0.2em] underline inline-flex items-center gap-1"
                        style={{ color: COLORS.secondary }}
                        data-testid={`order-link-${o.id}`}
                      >
                        Détail <ChevronRight className="w-3 h-3" />
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* TAB FACTURES */}
      {activeTab === "factures" && customer.email_verified && (
        <div data-testid="tab-content-factures">
          {invoiceSummary.count_open > 0 && (
            <div
              className="p-4 mb-4 flex items-center justify-between gap-4 flex-wrap"
              style={{ background: "rgba(229,77,46,0.08)", border: "1px solid rgba(229,77,46,0.35)" }}
              data-testid="invoices-summary-open"
            >
              <div>
                <p className="text-xs uppercase tracking-[0.2em]" style={{ color: COLORS.chili }}>Solde à régler</p>
                <p className="text-2xl mt-1 font-mono" style={{ color: COLORS.primary }}>
                  {invoiceSummary.total_open.toFixed(2)} <span className="text-sm" style={{ color: COLORS.secondary }}>CHF</span>
                </p>
              </div>
              <p className="text-xs" style={{ color: COLORS.secondary }}>
                {invoiceSummary.count_open} facture{invoiceSummary.count_open > 1 ? "s" : ""} en attente de paiement
              </p>
            </div>
          )}

          {invoices === null && (
            <div className="flex items-center gap-2 text-sm py-8" style={{ color: COLORS.secondary }}>
              <Loader2 className="w-4 h-4 animate-spin" /> Chargement de vos factures…
            </div>
          )}
          {invoices && invoices.length === 0 && (
            <div
              className="p-8 text-sm text-center"
              style={{ background: COLORS.surface, border: "1px solid rgba(242,236,228,0.06)", color: COLORS.secondary }}
              data-testid="invoices-empty"
            >
              {isReseller
                ? "Aucune facture émise pour le moment. Vos factures B2B apparaîtront ici une fois vos commandes traitées."
                : "Aucune facture émise pour vos commandes (vous payez en ligne lors du checkout)."}
            </div>
          )}
          {invoices && invoices.length > 0 && (
            <div style={{ background: COLORS.surface, border: "1px solid rgba(242,236,228,0.06)" }}>
              <ul className="divide-y" style={{ borderColor: "rgba(242,236,228,0.08)" }}>
                {invoices.map((inv) => (
                  <li
                    key={inv.id}
                    className="p-4 md:p-5 flex items-center justify-between gap-4 flex-wrap"
                    data-testid={`invoice-row-${inv.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-mono" style={{ color: COLORS.primary }}>{inv.reference || "—"}</p>
                        <InvoiceStatusBadge status={inv.status} />
                      </div>
                      <p className="text-xs mt-1" style={{ color: COLORS.muted }}>
                        Émise le {inv.issue_date ? new Date(inv.issue_date).toLocaleDateString("fr-CH") : "—"}
                        {inv.due_date && (
                          <> · Échéance {new Date(inv.due_date).toLocaleDateString("fr-CH")}</>
                        )}
                        {inv.order_reference && (
                          <> · Commande <span className="font-mono">{inv.order_reference}</span></>
                        )}
                      </p>
                    </div>
                    <div className="text-right shrink-0 flex items-center gap-4">
                      <p className="font-mono text-sm" style={{ color: COLORS.gold }}>{(inv.total_ttc || 0).toFixed(2)} CHF</p>
                      <Link
                        to={`/boutique/compte/factures/${inv.id}`}
                        className="text-xs uppercase tracking-[0.2em] underline inline-flex items-center gap-1"
                        style={{ color: COLORS.secondary }}
                        data-testid={`invoice-link-${inv.id}`}
                      >
                        <FileText className="w-3 h-3" /> Détail
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Phase 101fp — TAB MES TARIFS B2B (revendeurs uniquement) */}
      {activeTab === "b2b" && isReseller && customer.email_verified && (
        <B2BCatalogTab
          catalog={b2bCatalog}
          search={b2bSearch}
          setSearch={setB2bSearch}
          category={b2bCategory}
          setCategory={setB2bCategory}
        />
      )}
    </section>
  );
}

// ============== Phase 101fp — Bloc catalogue B2B (mes tarifs négociés) ==============
function B2BCatalogTab({ catalog, search, setSearch, category, setCategory }) {
  const items = catalog?.items || [];
  const categories = React.useMemo(() => {
    const set = new Set();
    for (const it of items) if (it.category) set.add(it.category);
    return Array.from(set).sort();
  }, [items]);
  const stats = React.useMemo(() => {
    let sumSaving = 0;
    let overrides = 0;
    let discounts = 0;
    let wholesales = 0;
    let fallbacks = 0;
    for (const it of items) {
      sumSaving += it.saving_amount || 0;
      if (it.price_source === "override") overrides += 1;
      else if (it.price_source === "discount") discounts += 1;
      else if (it.price_source === "wholesale") wholesales += 1;
      else fallbacks += 1;
    }
    return { sumSaving, overrides, discounts, wholesales, fallbacks };
  }, [items]);

  if (catalog === null) {
    return (
      <div className="flex items-center gap-2 text-sm py-8" data-testid="tab-content-b2b-loading" style={{ color: COLORS.secondary }}>
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement de vos tarifs négociés…
      </div>
    );
  }
  const q = search.trim().toLowerCase();
  const filtered = items.filter((it) => {
    if (category !== "__all__" && it.category !== category) return false;
    if (!q) return true;
    return (
      (it.name || "").toLowerCase().includes(q) ||
      (it.sku || "").toLowerCase().includes(q)
    );
  });

  return (
    <div data-testid="tab-content-b2b">
      {/* En-tête reseller */}
      <div
        className="p-4 md:p-5 mb-4"
        style={{
          background: "linear-gradient(90deg, rgba(136,160,102,0.15) 0%, rgba(193,160,99,0.10) 100%)",
          border: `1px solid ${COLORS.gold}40`,
        }}
        data-testid="b2b-header-card"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em]" style={{ color: COLORS.gold }}>
              Compte revendeur B2B
            </p>
            <h3 className="mt-1 text-lg" style={{ color: COLORS.primary, fontFamily: "Georgia, serif" }}>
              {catalog.reseller?.name || "—"}
            </h3>
            <p className="text-xs mt-1" style={{ color: COLORS.secondary }}>
              {catalog.total} produit{catalog.total > 1 ? "s" : ""} au catalogue
              {catalog.reseller?.discount_percent != null && (
                <> · Remise globale <span style={{ color: COLORS.basil }}>−{catalog.reseller.discount_percent}%</span></>
              )}
              {stats.overrides > 0 && (
                <> · <span style={{ color: COLORS.gold }}>{stats.overrides} prix négocié{stats.overrides > 1 ? "s" : ""}</span></>
              )}
            </p>
          </div>
          <Link
            to="/boutique"
            className="text-xs uppercase tracking-[0.2em] px-3 py-2 inline-flex items-center gap-2"
            style={{ background: COLORS.gold, color: COLORS.bg, fontWeight: 600 }}
            data-testid="b2b-go-shop"
          >
            <Store className="w-3 h-3" /> Commander maintenant
          </Link>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid="b2b-filters">
        <div className="relative flex-1 max-w-md min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: COLORS.muted }} />
          <input
            type="text"
            placeholder="Rechercher un plat ou SKU…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-3 py-2 text-sm"
            style={{
              background: COLORS.surface,
              border: "1px solid rgba(242,236,228,0.10)",
              color: COLORS.primary,
            }}
            data-testid="b2b-search"
          />
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="px-3 py-2 text-sm"
          style={{
            background: COLORS.surface,
            border: "1px solid rgba(242,236,228,0.10)",
            color: COLORS.primary,
          }}
          data-testid="b2b-category-filter"
        >
          <option value="__all__">Toutes catégories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className="text-xs" style={{ color: COLORS.secondary }}>
          {filtered.length} / {items.length} affichés
        </span>
      </div>

      {/* Liste produits */}
      {filtered.length === 0 ? (
        <div
          className="p-8 text-sm text-center"
          style={{ background: COLORS.surface, border: "1px solid rgba(242,236,228,0.06)", color: COLORS.secondary }}
          data-testid="b2b-empty"
        >
          Aucun produit ne correspond à votre recherche.
        </div>
      ) : (
        <div style={{ background: COLORS.surface, border: "1px solid rgba(242,236,228,0.06)" }}>
          <ul className="divide-y" style={{ borderColor: "rgba(242,236,228,0.08)" }}>
            {filtered.map((p) => (
              <li
                key={p.id}
                className="p-4 flex items-start gap-4 flex-wrap"
                data-testid={`b2b-row-${p.id}`}
              >
                {p.photo_url ? (
                  <img
                    src={p.photo_url}
                    alt=""
                    className="w-16 h-16 object-cover shrink-0"
                    style={{ background: COLORS.bg }}
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                ) : (
                  <div
                    className="w-16 h-16 flex items-center justify-center shrink-0"
                    style={{ background: COLORS.bg }}
                  >
                    <Package className="w-6 h-6" style={{ color: COLORS.muted }} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <p className="text-sm" style={{ color: COLORS.primary, fontWeight: 500 }}>
                      {p.name}
                    </p>
                    {p.category && (
                      <span
                        className="text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5"
                        style={{ background: "rgba(193,160,99,0.10)", color: COLORS.gold }}
                      >
                        {p.category}
                      </span>
                    )}
                    {p.spice_level != null && p.spice_level > 0 && (
                      <span className="inline-flex items-center gap-0.5" title={`Piquant ${p.spice_level}/3`} style={{ color: COLORS.chili }}>
                        {Array.from({ length: p.spice_level }).map((_, i) => (
                          <Flame key={i} className="w-3 h-3 fill-current" />
                        ))}
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] mt-0.5" style={{ color: COLORS.muted }}>
                    {p.sku}{p.net_weight ? ` · ${p.net_weight}${p.net_weight_unit || "g"}` : ""}
                  </p>
                </div>
                <div className="text-right shrink-0 flex flex-col items-end">
                  <div className="flex items-baseline gap-2">
                    <span className="font-mono text-lg" style={{ color: COLORS.gold, fontWeight: 500 }}>
                      {p.price_b2b.toFixed(2)}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.15em]" style={{ color: COLORS.muted }}>CHF</span>
                  </div>
                  {p.saving_amount > 0 ? (
                    <div className="text-[11px] mt-1 flex items-baseline gap-1.5" style={{ color: COLORS.secondary }}>
                      <span className="line-through" title="Prix public standard">{p.price_public.toFixed(2)} CHF</span>
                      <span style={{ color: COLORS.basil, fontWeight: 500 }}>
                        −{p.saving_amount.toFixed(2)}
                        {p.saving_pct > 0 && ` (−${p.saving_pct.toFixed(0)}%)`}
                      </span>
                    </div>
                  ) : (
                    <div className="text-[11px] mt-1" style={{ color: COLORS.muted }}>Prix public</div>
                  )}
                  <div className="text-[9px] uppercase tracking-[0.15em] mt-1" style={{ color: COLORS.muted }}>
                    {p.price_source === "override" && "Négocié"}
                    {p.price_source === "discount" && `Remise ${p.discount_pct}%`}
                    {p.price_source === "wholesale" && "Tarif B2B"}
                    {(p.price_source === "fallback_public" || !p.price_source) && "Sans B2B"}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    NOUVELLE: { label: "Nouvelle", color: COLORS.muted },
    PAIEMENT_EN_ATTENTE: { label: "Paiement en attente", color: COLORS.gold },
    PAYEE: { label: "Payée", color: COLORS.basil },
    PAIEMENT_ECHOUE: { label: "Paiement échoué", color: COLORS.chili },
    LIVREE: { label: "Livrée", color: COLORS.basil },
    FACTUREE: { label: "Facturée", color: COLORS.gold },
    EN_PRODUCTION: { label: "En production", color: COLORS.gold },
    PRETE: { label: "Prête", color: COLORS.basil },
    VALIDEE: { label: "Validée", color: COLORS.basil },
    ANNULEE: { label: "Annulée", color: COLORS.muted },
  };
  const m = map[status] || { label: status, color: COLORS.muted };
  return <span style={{ color: m.color }}>{m.label}</span>;
}

function InvoiceStatusBadge({ status }) {
  const map = {
    BROUILLON: { label: "Brouillon", color: COLORS.muted, bg: "rgba(115,115,115,0.10)" },
    ENVOYEE: { label: "À payer", color: COLORS.gold, bg: "rgba(193,160,99,0.10)" },
    PAYEE: { label: "Payée", color: COLORS.basil, bg: "rgba(136,160,102,0.10)" },
    PARTIELLE: { label: "Partiellement payée", color: COLORS.gold, bg: "rgba(193,160,99,0.10)" },
    EN_RETARD: { label: "En retard", color: COLORS.chili, bg: "rgba(229,77,46,0.10)" },
    ANNULEE: { label: "Annulée", color: COLORS.muted, bg: "rgba(115,115,115,0.10)" },
  };
  const m = map[status] || { label: status, color: COLORS.muted, bg: "rgba(115,115,115,0.10)" };
  return (
    <span
      className="text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5"
      style={{ background: m.bg, color: m.color, border: `1px solid ${m.color}` }}
    >
      {m.label}
    </span>
  );
}
